import { z } from 'zod';
/** One switch for the organisation vocabulary while the client/supplier decision is open. */
export const CLIENT_DISPLAY_NAME = 'Client';
export const BillingAddressSchema = z.object({
    line1: z.string().default(''),
    line2: z.string().optional(),
    suburb: z.string().default(''),
    state: z.string().default(''),
    postcode: z.string().default(''),
    country: z.string().default('Australia'),
});
export const ClientSchema = z.object({
    clientId: z.string().regex(/^[a-z0-9-]+$/),
    name: z.string().min(1),
    shortName: z.string().min(1),
    abn: z.string().default(''),
    billingAddress: BillingAddressSchema,
    serviceM8CompanyUuid: z.string().default(''),
    invoiceEmail: z.union([z.string().email(), z.literal('')]).default(''),
    invoiceCcList: z.array(z.string().email()).default([]),
    feedConfigId: z.string().min(1),
    /**
     * Other feeds this client also sends, for auto-detection only.
     *
     * A client can have more than one layout in play at once. Kerfoot's workbook
     * carries their working sheet AND the CER export it was generated from, and
     * either may arrive on its own as a CSV; without this, detecting the
     * non-primary one finds no client and the upload is rejected. `feedConfigId`
     * stays the single primary — it is what an explicit clientId resolves to.
     */
    alternateFeedConfigIds: z.array(z.string().min(1)).optional(),
    /**
     * Badges this client's ServiceM8 jobs carry, on top of the global
     * SM8_JOB_BADGE_UUIDS every job gets.
     *
     * A badge is not always a property of the work — it can be a property of who
     * the work is for, and one global list cannot say that. TechSafe's jobs all
     * carry a second badge that no other client's do; ours arrived without it and
     * looked different on the board from the 679 already there.
     *
     * Additive only. Badges are never replaced on an existing job, because the
     * field holds the whole set and 4,704 jobs carry badges we did not put there.
     */
    serviceM8BadgeUuids: z.array(z.string().min(1)).optional(),
    rateCardId: z.string().nullable().default(null),
    invoicingNotes: z.string().default(''),
    active: z.boolean().default(true),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const UpsertClientSchema = ClientSchema.omit({ createdAt: true, updatedAt: true });
/** Safe pre-production seeds. Commercial fields stay blank until confirmed by the client. */
export const SEEDED_CLIENTS = [
    {
        clientId: 'kerfoot',
        name: 'Kerfoot Pty Ltd',
        shortName: 'Kerfoot',
        abn: '',
        billingAddress: {
            line1: '1/30 Foundry Road',
            suburb: 'Seven Hills',
            state: 'NSW',
            postcode: '2147',
            country: 'Australia',
        },
        serviceM8CompanyUuid: 'c88976a5-519c-4edc-8800-1e1170226cab',
        invoiceEmail: '',
        invoiceCcList: [],
        // The working sheet, not the CER export beside it: it carries Kerfoot's
        // live status and is the one they keep editing after sending it. The
        // export is an alternate so a standalone upload of it still resolves —
        // but TechSafe sends that same layout, so an export arriving on its own
        // no longer auto-detects to a client. The operator picks.
        feedConfigId: 'kerfoot-tranche-management',
        alternateFeedConfigIds: ['cer-portal-export'],
        rateCardId: null,
        invoicingNotes: '',
        active: true,
    },
    {
        clientId: 'techsafe',
        name: 'TechSafe Australia',
        shortName: 'TechSafe',
        abn: '',
        /**
         * Read off their existing ServiceM8 jobs rather than invented: every job
         * we create for them must bill the same place the 1,175 already there do.
         */
        billingAddress: {
            line1: 'PO Box 33',
            suburb: 'Glen Waverley',
            state: 'VIC',
            postcode: '3150',
            country: 'Australia',
        },
        /**
         * "Tech Safe Australia" — the LIVE record, carrying their work from
         * 2025-01 to today. ServiceM8 also holds an inactive "Techsafe" with 1,757
         * older jobs (to 2025-11) and two near-empty duplicates; creating against
         * any of those would file the work where nobody is looking.
         */
        serviceM8CompanyUuid: 'b98078f7-54b7-4c48-a689-2250afb6aa1b',
        invoiceEmail: '',
        invoiceCcList: [],
        // TechSafe send the CER portal export verbatim — the same layout Kerfoot's
        // workbook carries on its second sheet. They sat on the `client-a` demo
        // parser until their first real file arrived, which is why that file
        // auto-detected into Kerfoot instead: Kerfoot was the only client claiming
        // the export. Both claim it now, so uploads of it need the client named.
        // `client-a` stays as an alternate — nobody else uses it, so the trial
        // batches assembled against it still resolve without being named.
        feedConfigId: 'cer-portal-export',
        alternateFeedConfigIds: ['client-a'],
        /**
         * On 679 of 679 of their 2026 work orders, and on 2,641 of the 2,717 jobs
         * that carry it tenant-wide. The remaining ~76 are 14-19 a year spread
         * across six years and every other client, which reads as occasional
         * hand-tagging rather than a second meaning for the badge.
         */
        serviceM8BadgeUuids: ['bfc40bab-0b05-4bdd-ae2d-1d76c78ef66b'],
        rateCardId: null,
        invoicingNotes: '',
        active: true,
    },
    {
        clientId: 'gses',
        name: 'Global Sustainable Energy Solutions',
        shortName: 'GSES',
        abn: '',
        billingAddress: {
            line1: 'Suite 4, Level 3',
            line2: '44-54 Botany Road',
            suburb: 'Alexandria',
            state: 'NSW',
            postcode: '2015',
            country: 'Australia',
        },
        /** Their live record. Two dormant duplicates hold 0 and 2 jobs. */
        serviceM8CompanyUuid: '2d95c2d4-51dd-4252-8937-1d919a78ba1b',
        invoiceEmail: '',
        invoiceCcList: [],
        feedConfigId: 'gses-allocation',
        rateCardId: null,
        invoicingNotes: '',
        active: true,
    },
    {
        clientId: 'itp',
        name: 'ITP Renewables',
        shortName: 'ITP',
        abn: '',
        billingAddress: {
            line1: 'GPO Box 1553',
            suburb: 'Canberra',
            state: 'ACT',
            postcode: '2601',
            country: 'Australia',
        },
        /**
         * "IT Power (Australia) Pty Ltd", their live record. The separate "ITP"
         * company holds 4 jobs and stops at 2026-02.
         */
        serviceM8CompanyUuid: 'cd53f8c1-fea2-41e8-ae5c-1d418181be6b',
        invoiceEmail: '',
        invoiceCcList: [],
        feedConfigId: 'itp-allocation',
        rateCardId: null,
        invoicingNotes: '',
        active: true,
    },
    {
        clientId: 'mea',
        name: 'Master Electricians Australia',
        shortName: 'MEA',
        abn: '',
        /**
         * No postcode, because their 209 existing jobs carry none. Read off those
         * rather than completed from the suburb: the point of this field is that
         * a job we create bills the same place the ones already there do, and
         * "improving" it here would make ours the odd ones out.
         */
        billingAddress: {
            line1: 'PO Box 2438',
            suburb: 'Fortitude Valley',
            state: 'QLD',
            postcode: '',
            country: 'Australia',
        },
        /**
         * "Master Electrician" — their live record, to 2026-08. A separate "MEA"
         * company holds 2 jobs billed to a residential address in SA and is not
         * the organisation.
         */
        serviceM8CompanyUuid: 'da1a3dba-82d7-403b-933b-1e520cc573bb',
        invoiceEmail: '',
        invoiceCcList: [],
        feedConfigId: 'generic',
        rateCardId: null,
        invoicingNotes: '',
        active: true,
    },
    {
        clientId: 'smart-energy',
        name: 'Smart Energy Council',
        shortName: 'Smart Energy',
        abn: '',
        /** Read from the active ServiceM8 company used by all 15 codes in the
         * 2026-08 Smart Energy allocation (9 work orders, including 6 combos).
         * Four similarly named historical records still exist; do not substitute
         * one of those without first checking where current work is filed. */
        billingAddress: {
            line1: 'PO Box 231',
            suburb: 'Mawson',
            state: 'ACT',
            postcode: '2607',
            country: 'Australia',
        },
        serviceM8CompanyUuid: '32dde459-7f66-4349-9207-1d837a9e175b',
        invoiceEmail: '',
        invoiceCcList: [],
        feedConfigId: 'smart-energy-allocation',
        rateCardId: null,
        invoicingNotes: '',
        active: true,
    },
];
export function clientDisplayName(client, compact = false) {
    return compact ? client.shortName : client.name;
}
//# sourceMappingURL=clients.js.map